import { Injectable } from '@angular/core';

/**
 * Log Level Enumeration
 */
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
  NONE = 'none',
}

/**
 * Logger Configuration
 */
export interface LoggerConfig {
  level: LogLevel;
  enableConsoleLogging: boolean;
  enableRemoteLogging: boolean;
  context?: string;
}

/**
 * Default Logger Configuration
 */
const DEFAULT_LOGGER_CONFIG: LoggerConfig = {
  level: LogLevel.INFO,
  enableConsoleLogging: true,
  enableRemoteLogging: false,
};

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private config: LoggerConfig = DEFAULT_LOGGER_CONFIG;
  private readonly levelHierarchy: Record<LogLevel, number> = {
    [LogLevel.DEBUG]: 0,
    [LogLevel.INFO]: 1,
    [LogLevel.WARN]: 2,
    [LogLevel.ERROR]: 3,
    [LogLevel.NONE]: 4,
  };

  /**
   * Configure the logger
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Log a debug message
   */
  debug(message: string, data?: unknown, context?: string): void {
    this.log(LogLevel.DEBUG, message, data, context);
  }

  /**
   * Log an info message
   */
  info(message: string, data?: unknown, context?: string): void {
    this.log(LogLevel.INFO, message, data, context);
  }

  /**
   * Log a warning message
   */
  warn(message: string, data?: unknown, context?: string): void {
    this.log(LogLevel.WARN, message, data, context);
  }

  /**
   * Log an error message
   */
  error(message: string, error?: Error | unknown, context?: string): void {
    this.log(LogLevel.ERROR, message, error, context);
  }

  /**
   * Create a child logger with a specific context
   */
  createChild(context: string): LoggerService {
    const childLogger = new LoggerService();
    childLogger.configure({ ...this.config, context });
    return childLogger;
  }

  /**
   * Internal logging method
   */
  private log(
    level: LogLevel,
    message: string,
    data?: unknown,
    context?: string
  ): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const logEntry = this.createLogEntry(level, message, data, context);

    if (this.config.enableConsoleLogging) {
      this.logToConsole(level, logEntry);
    }

    if (this.config.enableRemoteLogging) {
      this.logToRemote(logEntry);
    }
  }

  /**
   * Check if the message should be logged based on current level
   */
  private shouldLog(level: LogLevel): boolean {
    return this.levelHierarchy[level] >= this.levelHierarchy[this.config.level];
  }

  /**
   * Create a structured log entry
   */
  private createLogEntry(
    level: LogLevel,
    message: string,
    data?: unknown,
    context?: string
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: context || this.config.context,
      data,
    };
  }

  /**
   * Log to browser console
   */
  private logToConsole(level: LogLevel, entry: LogEntry): void {
    const prefix = `[${entry.timestamp}] [${entry.level.toUpperCase()}]${
      entry.context ? ` [${entry.context}]` : ''
    }`;

    switch (level) {
      case LogLevel.DEBUG:
        console.debug(prefix, entry.message, entry.data || '');
        break;
      case LogLevel.INFO:
        console.info(prefix, entry.message, entry.data || '');
        break;
      case LogLevel.WARN:
        console.warn(prefix, entry.message, entry.data || '');
        break;
      case LogLevel.ERROR:
        console.error(prefix, entry.message, entry.data || '');
        break;
    }
  }

  /**
   * Log to remote service (placeholder for future implementation)
   */
  private logToRemote(entry: LogEntry): void {
    // TODO: Implement remote logging (e.g., to Application Insights, Sentry, etc.)
    // This would typically send logs to a backend service
    console.debug('[Remote Logging]', entry);
  }
}

/**
 * Log Entry Structure
 */
interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: string;
  data?: unknown;
}
